import { useEffect, useState } from "react";

/**
 * Fixed-position section dots along the right edge.
 * Tracks which `[data-spy-section]` is currently in view via IntersectionObserver
 * and lets users jump to any section by clicking its dot.
 */
export default function ScrollSpyDots({ sections }) {
  const [active, setActive] = useState(sections[0]?.id);

  // Track the section closest to the top-third of the viewport. We do this
  // with a throttled scroll listener instead of IntersectionObserver because
  // some of our sections are much taller than the viewport — an IO with a
  // shrunken rootMargin would never reach its threshold for them and the
  // active dot would stop updating mid-section.
  useEffect(() => {
    const compute = () => {
      const els = sections
        .map((s) => ({ s, el: document.getElementById(s.id) }))
        .filter(({ el }) => el);
      if (els.length === 0) return;
      // Trigger line: 30% from the top of the viewport.
      const trigger = window.innerHeight * 0.3;
      let current = els[0].s.id;
      for (const { s, el } of els) {
        const rect = el.getBoundingClientRect();
        if (rect.top - trigger <= 0) current = s.id;
        else break;
      }
      // If we're past the bottom of the page, lock to the last section.
      if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 4) {
        current = els[els.length - 1].s.id;
      }
      setActive((prev) => (prev === current ? prev : current));
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        compute();
        ticking = false;
      });
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sections]);

  const jump = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      aria-label="Page sections"
      className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col items-end gap-3"
      data-testid="scroll-spy-dots"
    >
      {sections.map((s) => {
        const isActive = active === s.id;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => jump(s.id)}
            className="group flex items-center gap-2 cursor-pointer"
            data-testid={`spy-dot-${s.id}`}
            aria-label={`Jump to ${s.label}`}
            aria-current={isActive ? "true" : undefined}
          >
            <span
              className={`text-[10px] uppercase tracking-[0.22em] font-semibold transition-all duration-300 ${
                isActive
                  ? "opacity-100 text-amber-800"
                  : "opacity-0 group-hover:opacity-70 text-stone-600"
              }`}
            >
              {s.label}
            </span>
            <span
              className={`block rounded-full transition-all duration-300 ${
                isActive
                  ? "w-2.5 h-6 bg-amber-500 shadow-[0_0_0_3px_rgba(251,191,36,0.18)]"
                  : "w-2 h-2 bg-stone-400/60 group-hover:bg-amber-500/70"
              }`}
            />
          </button>
        );
      })}
    </nav>
  );
}
